// maths.portll.net — A4 print view for one task.
//
// The grown-up's page: title, hook, what you'll need, how to run it, and the card you
// read aloud to your child — followed by <PrintInsert/>, the child's black-on-white
// recording sheet (its own page under print). Loads the task straight from the public
// `tasks/{id}` doc (readable to any signed-in family; the route already sits behind the
// auth + beta guards). No QR library — we just print the plain completion URL so a
// grown-up can type it in later. Uses the shared .print-sheet / .no-print conventions.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase.js";
import { yearLabel } from "../lib/tasks.js";
import PrintInsert from "../components/PrintInsert.jsx";
import {
  Button,
  Card,
  Spinner,
  StrandBadge,
  Chip,
  EmptyState,
  injectStyles,
} from "../components/ui.jsx";

// Canonical host for the printed link — the real origin in prod, the product domain
// when running on localhost (so a test printout never says "127.0.0.1").
function canonicalHost() {
  if (typeof window !== "undefined" && window.location) {
    const h = window.location.hostname || "";
    if (h && !/^(localhost$|127\.|\[?::1)/.test(h)) return window.location.host;
  }
  return "maths.portll.net";
}

export default function PrintSheet() {
  const { childId, taskId } = useParams();
  const [task, setTask] = useState(null);
  const [status, setStatus] = useState("loading"); // "loading" | "ready" | "missing"

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    (async () => {
      try {
        const snap = await getDoc(doc(db, "tasks", taskId));
        if (!alive) return;
        if (snap.exists()) {
          setTask({ id: snap.id, ...snap.data() });
          setStatus("ready");
        } else {
          setStatus("missing");
        }
      } catch {
        if (alive) setStatus("missing");
      }
    })();
    return () => {
      alive = false;
    };
  }, [taskId]);

  // A friendlier print filename / header while this page is open.
  useEffect(() => {
    if (!task) return;
    const prev = document.title;
    document.title = `${task.title || "Task"} — maths.portll.net`;
    return () => {
      document.title = prev;
    };
  }, [task]);

  if (status === "loading") {
    return (
      <main className="ps-load">
        <Spinner size={28} label="Loading task" />
      </main>
    );
  }

  if (status === "missing" || !task) {
    return (
      <main className="ps-load">
        <EmptyState
          icon="🔍"
          title="We couldn’t find that task"
          hint="It may have been unpublished. Head back and pick another activity."
          action={
            <Button
              to={`/grown-ups/child/${childId}/task/${taskId}`}
              variant="secondary"
            >
              ← Back to the task
            </Button>
          }
        />
      </main>
    );
  }

  const materials = Array.isArray(task.materials) ? task.materials : [];
  const steps = Array.isArray(task.parentInstructions) ? task.parentInstructions : [];
  const completePath = `/grown-ups/child/${childId}/task/${taskId}/complete`;
  const completeUrl = `${canonicalHost()}${completePath}`;

  return (
    <main className="ps-wrap">
      <div className="ps-toolbar no-print">
        <Button
          to={`/grown-ups/child/${childId}/task/${taskId}`}
          variant="secondary"
          size="sm"
        >
          ← Back
        </Button>
        <Button variant="primary" onClick={() => window.print()}>
          🖨️ Print
        </Button>
      </div>

      {/* Grown-up instructions sheet */}
      <section className="print-sheet ps-sheet" aria-label="Activity sheet">
        <header className="ps-head">
          <h1 className="ps-title">{task.title || "Maths activity"}</h1>
          {task.hook && <p className="ps-hook">{task.hook}</p>}
          <div className="ps-meta">
            {task.yearLevel != null && (
              <Chip tone="neutral">Year {yearLabel(task.yearLevel)}</Chip>
            )}
            {task.strand && <StrandBadge strand={task.strand} />}
            {Number.isFinite(task.estimatedMinutes) && (
              <Chip tone="neutral">About {task.estimatedMinutes} min</Chip>
            )}
            {task.setting && <Chip tone="neutral">{String(task.setting).replace(/_/g, " ")}</Chip>}
          </div>
        </header>

        {task.whyThisMatters && (
          <p className="ps-why">
            <strong>Why this one:</strong> {task.whyThisMatters}
          </p>
        )}

        {materials.length > 0 && (
          <section className="ps-block">
            <h2 className="ps-h2">What you’ll need</h2>
            <ul className="ps-materials">
              {materials.map((m, i) => (
                <li key={i}>
                  <span className="ps-check" aria-hidden="true">▢</span>
                  <span>
                    {m.item}
                    {m.optional ? <span className="ps-opt"> (optional)</span> : null}
                    {m.substitute ? (
                      <span className="ps-sub"> — or use {m.substitute}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {steps.length > 0 && (
          <section className="ps-block">
            <h2 className="ps-h2">How to do it together</h2>
            <ol className="ps-steps">
              {steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </section>
        )}

        {task.kidPromptCard && (
          <section className="ps-block">
            <h2 className="ps-h2">Read this to your child</h2>
            <blockquote className="ps-prompt">
              <span className="ps-prompt__mark" aria-hidden="true">“</span>
              {task.kidPromptCard}
            </blockquote>
          </section>
        )}

        {task.safetyNote && (
          <p className="ps-safety">
            <strong>Stay safe:</strong> {task.safetyNote}
          </p>
        )}

        <footer className="ps-foot">
          <p className="ps-done">
            <strong>All done together?</strong> Tap “We did it” in the app, or type this into
            your browser:
          </p>
          <p className="ps-url">
            <code>{completeUrl}</code>
          </p>
        </footer>
      </section>

      {/* Child's recording sheet — its own page under print */}
      {task.datasetSpec && (
        <div className="ps-insert">
          <PrintInsert task={task} />
        </div>
      )}
    </main>
  );
}

const PS_CSS = `
.ps-load{min-height:60vh;display:grid;place-items:center;padding:2rem;}
.ps-wrap{max-width:820px;margin:0 auto;padding:1.25rem;}
.ps-toolbar{display:flex;align-items:center;justify-content:space-between;gap:.75rem;margin-bottom:1rem;}

.ps-sheet{background:var(--card);color:var(--ink);border:1px solid var(--line);border-radius:var(--radius);
  padding:1.5rem 1.6rem;box-shadow:var(--shadow);}
.ps-head{border-bottom:2px solid var(--brand);padding-bottom:.9rem;margin-bottom:1rem;}
.ps-title{margin:0;font-size:clamp(1.5rem,4vw,2rem);}
.ps-hook{margin:.4rem 0 0;font-size:1.05rem;color:var(--ink-soft);font-style:italic;}
.ps-meta{display:flex;flex-wrap:wrap;gap:.4rem;margin-top:.8rem;}

.ps-why{margin:0 0 1.1rem;color:var(--ink-soft);}
.ps-block{margin:0 0 1.2rem;}
.ps-h2{margin:0 0 .55rem;font-size:1.15rem;color:var(--brand-ink);}

.ps-materials{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:.4rem;}
.ps-materials li{display:flex;gap:.6rem;align-items:baseline;}
.ps-check{color:var(--brand);font-size:1.05rem;flex:0 0 auto;}
.ps-opt{color:var(--ink-soft);}
.ps-sub{color:var(--ink-soft);}

.ps-steps{margin:0;padding-left:1.4rem;display:flex;flex-direction:column;gap:.5rem;}
.ps-steps li{padding-left:.2rem;}

.ps-prompt{position:relative;margin:0;padding:1rem 1.1rem 1rem 2.4rem;
  background:color-mix(in srgb,var(--accent) 10%,var(--card));
  border:1px solid color-mix(in srgb,var(--accent) 30%,var(--line));border-radius:12px;
  font-size:1.1rem;font-weight:600;color:#7a4a10;}
.ps-prompt__mark{position:absolute;left:.7rem;top:.35rem;font-size:2rem;line-height:1;color:var(--accent);}

.ps-safety{margin:1rem 0 0;padding:.7rem .9rem;border-radius:10px;
  background:color-mix(in srgb,var(--danger) 8%,var(--card));
  border:1px solid color-mix(in srgb,var(--danger) 26%,var(--line));color:var(--danger);font-size:.95rem;}

.ps-foot{margin-top:1.3rem;padding-top:.9rem;border-top:1px dashed var(--line);}
.ps-done{margin:0 0 .35rem;font-size:.95rem;}
.ps-url{margin:0;}
.ps-url code{display:inline-block;font-size:.9rem;word-break:break-all;
  background:color-mix(in srgb,var(--sky) 8%,var(--card));color:#1e4d63;
  border:1px solid color-mix(in srgb,var(--sky) 24%,var(--line));border-radius:6px;padding:.3rem .5rem;}

.ps-insert{margin-top:1.5rem;}

@media print{
  .ps-wrap{max-width:none;margin:0;padding:0;}
  .ps-sheet{border:none;border-radius:0;box-shadow:none;padding:0;background:#fff;color:#000;}
  .ps-head{border-bottom-color:#000;}
  .ps-h2,.ps-title{color:#000;}
  .ps-hook,.ps-why,.ps-opt,.ps-sub{color:#333;}
  .ps-prompt{background:#fff !important;border:1.5px solid #000;color:#000;}
  .ps-prompt__mark{color:#000;}
  .ps-safety{background:#fff !important;border:1.5px solid #000;color:#000;}
  .ps-url code{background:#fff !important;border:1px solid #000;color:#000;}
  .ps-check{color:#000;}
  .ps-insert{break-before:page;margin-top:0;}
}
`;

injectStyles("maths-printsheet-styles", PS_CSS);
